/**
 * ImageEditorModal.jsx - Image Editor with Crop and Filters
 * =========================================================
 * Features:
 * - Image cropping with aspect ratio presets
 * - Brightness, Contrast, Saturation adjustments
 * - Preview with real-time filter application
 * - Upload to R2 via backend API
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  X, Upload, Crop, Sun, Contrast, Palette, 
  RotateCcw, Check, Loader2, ImageIcon,
  Square, RectangleHorizontal, RectangleVertical
} from 'lucide-react';

const ASPECT_RATIOS = [
  { label: 'Libero', value: undefined, icon: Square },
  { label: '1:1', value: 1, icon: Square },
  { label: '16:9', value: 16/9, icon: RectangleHorizontal },
  { label: '4:3', value: 4/3, icon: RectangleHorizontal },
  { label: '3:4', value: 3/4, icon: RectangleVertical },
  { label: '9:16', value: 9/16, icon: RectangleVertical },
];

const ImageEditorModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialImage = null,
  title = 'Editor Immagine',
  aspectRatio = undefined,
  maxWidth = 1920,
  maxHeight = 1080
}) => {
  const [imageSrc, setImageSrc] = useState(initialImage);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [selectedAspect, setSelectedAspect] = useState(aspectRatio);
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100
  });
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('crop'); // crop | filters
  
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setImageSrc(initialImage);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setFilters({ brightness: 100, contrast: 100, saturation: 100 });
      setActiveTab('crop');
    }
  }, [isOpen, initialImage]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop(undefined);
      setCompletedCrop(undefined);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    
    // Set initial crop to center
    const { width, height } = e.currentTarget;
    const cropWidth = Math.min(width * 0.8, width);
    const cropHeight = selectedAspect 
      ? cropWidth / selectedAspect 
      : Math.min(height * 0.8, height);
    
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;
    
    setCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: Math.min(cropHeight, height)
    });
  }, [selectedAspect]);

  const getFilterStyle = () => ({
    filter: `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%)`
  });

  const resetFilters = () => {
    setFilters({ brightness: 100, contrast: 100, saturation: 100 });
  };

  const generateCroppedImage = async () => {
    if (!imgRef.current || !completedCrop) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const image = imgRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    // Set canvas size (respect max dimensions)
    let finalWidth = cropWidth;
    let finalHeight = cropHeight;
    
    if (finalWidth > maxWidth) {
      const ratio = maxWidth / finalWidth;
      finalWidth = maxWidth;
      finalHeight *= ratio;
    }
    if (finalHeight > maxHeight) {
      const ratio = maxHeight / finalHeight;
      finalHeight = maxHeight;
      finalWidth *= ratio;
    }

    canvas.width = finalWidth;
    canvas.height = finalHeight;

    // Apply filters
    ctx.filter = `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%)`;

    // Draw cropped image
    ctx.drawImage(
      image,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, finalWidth, finalHeight
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });
  };

  const handleSave = async () => {
    setUploading(true);
    
    try {
      let imageBlob;
      
      if (completedCrop && imgRef.current) {
        // Generate cropped/filtered image
        imageBlob = await generateCroppedImage();
      } else if (imageSrc && imageSrc.startsWith('data:')) {
        // Convert base64 to blob
        const res = await fetch(imageSrc);
        imageBlob = await res.blob();
      } else {
        // URL - just pass it through
        onSave(imageSrc);
        onClose();
        return;
      }

      if (!imageBlob) {
        throw new Error('Impossibile generare l\'immagine');
      }

      // Upload to R2 via backend
      const formData = new FormData();
      formData.append('file', imageBlob, 'image.jpg');

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/media/upload/cms-image`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload fallito');
      }

      const data = await response.json();
      onSave(data.url);
      onClose();
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Errore durante il salvataggio: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-[#1a1a18] rounded-xl border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button 
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left - Image Preview */}
          <div className="flex-1 p-4 flex flex-col items-center justify-center bg-[#0a0a09] min-h-[400px]">
            {!imageSrc ? (
              <div 
                className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#B08B5C] transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="w-16 h-16 text-white/20 mb-4" />
                <p className="text-white/40 mb-2">Clicca per caricare un'immagine</p>
                <p className="text-white/30 text-sm">JPG, PNG, WebP (max 10MB)</p>
              </div>
            ) : (
              <div className="relative max-w-full max-h-full overflow-auto">
                {activeTab === 'crop' ? (
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={selectedAspect}
                  >
                    <img
                      ref={imgRef}
                      src={imageSrc}
                      alt="Edit"
                      onLoad={onImageLoad}
                      style={{ 
                        maxHeight: '500px',
                        ...getFilterStyle()
                      }}
                    />
                  </ReactCrop>
                ) : (
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Preview"
                    style={{ 
                      maxHeight: '500px',
                      ...getFilterStyle()
                    }}
                  />
                )}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Hidden canvas for processing */}
            <canvas ref={canvasRef} className="hidden" />
          </div>

          {/* Right - Controls */}
          <div className="w-72 border-l border-white/10 p-4 flex flex-col">
            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('crop')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'crop' 
                    ? 'bg-[#B08B5C] text-black' 
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                <Crop className="w-4 h-4" />
                Ritaglia
              </button>
              <button
                onClick={() => setActiveTab('filters')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'filters' 
                    ? 'bg-[#B08B5C] text-black' 
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                <Sun className="w-4 h-4" />
                Filtri
              </button>
            </div>

            {activeTab === 'crop' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    Proporzioni
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {ASPECT_RATIOS.map((ratio) => {
                      const Icon = ratio.icon;
                      return (
                        <button
                          key={ratio.label}
                          onClick={() => setSelectedAspect(ratio.value)}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg text-xs transition-colors ${
                            selectedAspect === ratio.value
                              ? 'bg-[#B08B5C] text-black'
                              : 'bg-white/5 text-white/60 hover:bg-white/10'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {ratio.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-white/20 text-white/70"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Cambia immagine
                </Button>
              </div>
            )}

            {activeTab === 'filters' && (
              <div className="space-y-6">
                {/* Brightness */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-white/50 uppercase tracking-wider flex items-center gap-2">
                      <Sun className="w-3 h-3" />
                      Luminosità
                    </label>
                    <span className="text-xs text-white/40">{filters.brightness}%</span>
                  </div>
                  <Slider
                    value={[filters.brightness]}
                    onValueChange={([v]) => setFilters(f => ({ ...f, brightness: v }))}
                    min={50}
                    max={150}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Contrast */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-white/50 uppercase tracking-wider flex items-center gap-2">
                      <Contrast className="w-3 h-3" />
                      Contrasto
                    </label>
                    <span className="text-xs text-white/40">{filters.contrast}%</span>
                  </div>
                  <Slider
                    value={[filters.contrast]}
                    onValueChange={([v]) => setFilters(f => ({ ...f, contrast: v }))}
                    min={50}
                    max={150}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Saturation */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-white/50 uppercase tracking-wider flex items-center gap-2">
                      <Palette className="w-3 h-3" />
                      Saturazione
                    </label>
                    <span className="text-xs text-white/40">{filters.saturation}%</span>
                  </div>
                  <Slider
                    value={[filters.saturation]}
                    onValueChange={([v]) => setFilters(f => ({ ...f, saturation: v }))}
                    min={0}
                    max={200}
                    step={1}
                    className="w-full"
                  />
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="w-full border-white/20 text-white/70"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset filtri
                </Button>
              </div>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Actions */}
            <div className="space-y-2 pt-4 border-t border-white/10">
              <Button
                onClick={handleSave}
                disabled={!imageSrc || uploading}
                className="w-full bg-[#B08B5C] hover:bg-[#9a7a4f] text-black"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Salva immagine
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                className="w-full text-white/60"
              >
                Annulla
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditorModal;
